import { ExpressTier } from "data-types";
import { SHIPPING_COMPANY_UBER, SHIPPING_COMPANY_URBIFY } from "../constants";

export type ReconciliationResult =
  | { action: "keep" }
  | { action: "select"; tier: string }
  | { action: "regular" };

// Provider -> preferred express tiers (soonest first).
const PROVIDER_TIER_PRIORITY: Record<string, string[]> = {
  [SHIPPING_COMPANY_URBIFY]: ["same_day", "next_day"],
  [SHIPPING_COMPANY_UBER]: ["uber_2h"],
};

// Fallback order when the original provider is completely unavailable.
const PROVIDER_FALLBACK_ORDER = [SHIPPING_COMPANY_URBIFY, SHIPPING_COMPANY_UBER];

/**
 * Decide what to do with a cart that mounted express-pre-selected (e.g. a reorder) once the live
 * quote's tiers are known. Keep the seeded tier if it is still available; otherwise fall forward
 * to the best available tier of the same provider (same_day past cutoff -> next_day). When
 * `allowProviderFallback` is true (urbify reorders), cross-provider fallback is applied before
 * dropping to regular: urbify -> uber -> DHL.
 */
export const resolveSeededTierReconciliation = (params: {
  wasExpressPreSelected: boolean;
  seededTier: string | null | undefined;
  company: string | null | undefined;
  availableTiers: ExpressTier[];
  allowProviderFallback?: boolean;
}): ReconciliationResult => {
  const { wasExpressPreSelected, seededTier, company, availableTiers, allowProviderFallback } = params;
  if (!wasExpressPreSelected) return { action: "keep" };

  const seededAvailable = availableTiers.some(t => t.tier === seededTier && t.isAvailable);
  if (seededAvailable) return { action: "keep" };

  // First try within the same provider (tier fall-forward, e.g. same_day -> next_day)
  const sameProviderPriority = (company && PROVIDER_TIER_PRIORITY[company]) || [];
  for (const candidate of sameProviderPriority) {
    if (availableTiers.some(t => t.tier === candidate && t.isAvailable)) {
      return { action: "select", tier: candidate };
    }
  }

  if (allowProviderFallback) {
    // Cross-provider fallback: skip the original provider, try others in order
    for (const fallbackCompany of PROVIDER_FALLBACK_ORDER) {
      if (fallbackCompany === company) continue;
      const fallbackPriority = PROVIDER_TIER_PRIORITY[fallbackCompany] || [];
      for (const candidate of fallbackPriority) {
        if (availableTiers.some(t => t.tier === candidate && t.isAvailable)) {
          return { action: "select", tier: candidate };
        }
      }
    }
  }

  return { action: "regular" };
};
